/**
 * /students/:studentId — full student profile. Radar + identity card +
 * trend + timeline + admissions transcript.
 */
import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import {
  useStudent,
  useStudentAdmission,
  useStudentRadar,
  useStudentStats,
  type InterviewQuestionSlot,
  type Subject,
} from "../../api/school";
import { useAuth } from "@/auth/OidcProvider";
import { canManageSchool, useOrgMe } from "../../api/school";
import { LifecyclePanel } from "./LifecyclePanel";
import { StudentLevelsPanel } from "./StudentLevelsPanel";
import { TranscriptPanel } from "../gpa/TranscriptPanel";
import { ConductPanel } from "../gpa/ConductPanel";
import { RadarChart } from "../../components/RadarChart";
import { TrendChart } from "../../components/TrendChart";

const RATING_TONES: Record<string, string> = {
  fluent: "bg-emerald-100 text-emerald-700",
  ok: "bg-sky-100 text-sky-700",
  struggling: "bg-rose-100 text-rose-700",
};

const SUBJECT_TAB_COLORS: Record<Subject, string> = {
  english: "border-sky-500 text-sky-700 bg-sky-50",
  math: "border-amber-500 text-amber-700 bg-amber-50",
  chinese: "border-rose-500 text-rose-700 bg-rose-50",
};

const SUBJECTS: Subject[] = ["english", "math", "chinese"];

export function StudentDetailPage() {
  const { t, i18n } = useTranslation();
  const { studentId } = useParams<{ studentId: string }>();
  const [subject, setSubject] = useState<Subject>("english");
  const student = useStudent(studentId);
  const radar = useStudentRadar(studentId, subject);
  const stats = useStudentStats(studentId, subject);
  const admission = useStudentAdmission(studentId);
  const { state } = useAuth();
  const orgMe = useOrgMe();
  const canManage =
    (state.status === "authenticated" && state.user.isAdmin) || canManageSchool(orgMe.data);

  if (student.isLoading || radar.isLoading)
    return <p className="p-6 text-slate-500 italic">{t("common.loading")}</p>;
  if (student.error || radar.error)
    return (
      <div className="m-6 rounded border border-red-200 bg-red-50 p-4 text-sm text-red-700">
        {((student.error || radar.error) as Error).message}
      </div>
    );
  if (!student.data || !radar.data) return null;

  const s = student.data;
  const radarLang: "zh" | "en" = i18n.language.toLowerCase().startsWith("zh") ? "zh" : "en";
  const subjectLabel = t(`common.subjects.${subject}`);

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <header className="mb-6">
        <Link to="/classes" className="text-sm text-sky-600 hover:underline">
          {t("classes.studentDetail.back")}
        </Link>
        <div className="mt-2 flex items-baseline gap-3 flex-wrap">
          <h1 className="text-2xl font-semibold text-slate-800">{s.name_zh}</h1>
          {s.name_en && <span className="text-slate-500">({s.name_en})</span>}
          <StatusBadge status={s.status} />
        </div>
        <p className="text-sm text-slate-500 mt-1">
          {t("classes.studentDetail.header", {
            grade: s.grade,
            section: s.section
              ? t("classes.studentDetail.sectionPart", { section: s.section })
              : "",
            enrollment: s.enrollment_date
              ? t("classes.studentDetail.enrollmentPart", { date: s.enrollment_date })
              : "",
          })}
        </p>
      </header>

      <LifecyclePanel studentId={s.id} status={s.status} canManage={canManage} />

      <StudentLevelsPanel studentId={s.id} />

      <TranscriptPanel studentId={s.id} />

      <ConductPanel studentId={s.id} studentName={s.name_zh} />

      <section className="rounded-lg border border-slate-200 bg-white p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-semibold text-slate-600 uppercase tracking-wide">
            {t("classes.studentDetail.radarTitle")}
          </h2>
          <div className="inline-flex rounded border border-slate-200 overflow-hidden">
            {SUBJECTS.map((subj) => (
              <button
                key={subj}
                type="button"
                onClick={() => setSubject(subj)}
                className={`px-3 py-1 text-xs font-medium transition ${
                  subject === subj
                    ? `${SUBJECT_TAB_COLORS[subj]} border-b-2`
                    : "bg-white text-slate-500 hover:bg-slate-50"
                }`}
              >
                {t(`common.subjects.${subj}`)}
              </button>
            ))}
          </div>
        </div>
        <RadarChart data={radar.data} height={380} lang={radarLang} />
        <p className="mt-3 text-center text-sm text-slate-500">
          {t("classes.studentDetail.overallLabel")}
          <span className="font-mono text-lg text-slate-800 ml-2">
            {radar.data.overall ?? "—"}
          </span>
          {radar.data.overall === null && (
            <span className="text-slate-400 italic ml-2">
              {t("classes.studentDetail.noSubjectData", { subject: subjectLabel })}
            </span>
          )}
        </p>
      </section>

      <section className="rounded-lg border border-slate-200 bg-white p-6 mb-6">
        <h2 className="text-sm font-semibold text-slate-600 uppercase tracking-wide mb-4">
          {t("classes.studentDetail.subjectTrendTitle", { subject: subjectLabel })}
        </h2>
        {stats.isLoading && (
          <p className="text-slate-500 italic text-sm">{t("common.loading")}</p>
        )}
        {stats.data && stats.data.trend.length > 0 && (
          <TrendChart data={stats.data.trend} />
        )}
        {stats.data && stats.data.trend.length === 0 && (
          <p className="text-slate-400 italic text-sm text-center py-8">
            {t("classes.studentDetail.noScoresYet", { subject: subjectLabel })}
          </p>
        )}
      </section>

      <section className="rounded-lg border border-slate-200 bg-white p-6">
        <h2 className="text-sm font-semibold text-slate-600 uppercase tracking-wide mb-4">
          {t("classes.studentDetail.subjectTimelineTitle", { subject: subjectLabel })}
        </h2>
        {stats.data && stats.data.points.length > 0 ? (
          <ol className="space-y-3">
            {stats.data.points.map((p, i) => {
              const colorBySource: Record<string, string> = {
                admission: "border-purple-300 bg-purple-50 text-purple-700",
                assignment: "border-sky-300 bg-sky-50 text-sky-700",
                exam: "border-amber-300 bg-amber-50 text-amber-700",
                itarget: "border-emerald-300 bg-emerald-50 text-emerald-700",
              };
              return (
                <li key={i} className="flex gap-3 items-start">
                  <span
                    className={`text-xs px-2 py-0.5 rounded border ${
                      colorBySource[p.source] ?? "border-slate-300 bg-slate-50 text-slate-700"
                    }`}
                  >
                    {p.date}
                  </span>
                  <div className="flex-1">
                    <div className="text-sm text-slate-800">{p.label}</div>
                    {p.detail && (
                      <div className="text-xs text-slate-500 mt-0.5">{p.detail}</div>
                    )}
                  </div>
                  {p.value !== null && (
                    <span className="text-sm font-mono text-slate-700">{p.value}</span>
                  )}
                </li>
              );
            })}
          </ol>
        ) : (
          <p className="text-slate-400 italic text-sm text-center py-4">
            {t("classes.studentDetail.noRecordsYet", { subject: subjectLabel })}
          </p>
        )}
      </section>

      {admission.data && (
        <AdmissionInterviewSection
          admission={admission.data}
        />
      )}
    </div>
  );
}

// ── Admissions interview transcript ───────────────────────────────

function AdmissionInterviewSection({
  admission,
}: {
  admission: NonNullable<ReturnType<typeof useStudentAdmission>["data"]>;
}) {
  const { t } = useTranslation();
  const transcript = admission.interview_transcript ?? {};
  const decision = transcript.interviewer_decision;
  const recommendation = transcript.ai_recommendation;
  const rounds = transcript.rounds ?? [];
  const hasTranscript = rounds.length > 0;

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-6 mt-6">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h2 className="text-sm font-semibold text-slate-600 uppercase tracking-wide">
            {t("classes.studentDetail.admissionSection")}
          </h2>
          <p className="text-xs text-slate-500 mt-1">
            {t("classes.studentDetail.admissionMeta", {
              date: admission.interview_date,
              overall: admission.overall_score ?? "—",
            })}
          </p>
        </div>
        <div className="flex gap-2">
          {recommendation && (
            <span
              className={`text-xs px-2 py-1 rounded ${
                recommendation === "accept"
                  ? "bg-emerald-100 text-emerald-700"
                  : recommendation === "reject"
                    ? "bg-rose-100 text-rose-700"
                    : "bg-amber-100 text-amber-700"
              }`}
            >
              {t("classes.studentDetail.admissionAIPrefix")}
              {recommendation === "accept"
                ? t("admissions.interview.finalize.aiRecAccept")
                : recommendation === "reject"
                  ? t("admissions.interview.finalize.aiRecReject")
                  : t("admissions.interview.finalize.aiRecBorderline")}
            </span>
          )}
          {decision && (
            <span
              className={`text-xs px-2 py-1 rounded font-medium ${
                decision === "accept"
                  ? "bg-emerald-500 text-white"
                  : "bg-rose-500 text-white"
              }`}
            >
              {decision === "accept"
                ? t("classes.studentDetail.admissionDecisionAcceptedBadge")
                : t("classes.studentDetail.admissionDecisionRejectedBadge")}
            </span>
          )}
        </div>
      </div>

      {admission.interviewer_notes && (
        <div className="mb-4 p-3 rounded bg-slate-50 border border-slate-200 text-sm text-slate-700">
          <div className="text-xs text-slate-500 mb-1">{t("classes.studentDetail.interviewerNotesLabel")}</div>
          {admission.interviewer_notes}
        </div>
      )}

      {hasTranscript ? (
        <div className="space-y-4">
          {rounds.map((r) => (
            <div key={r.round}>
              <h3 className="text-xs font-medium uppercase tracking-wide text-slate-500 mb-2">
                {t("classes.studentDetail.roundLabel", { round: r.round })}
              </h3>
              <ul className="space-y-2">
                {r.questions.map((q: InterviewQuestionSlot, i: number) => (
                  <li
                    key={`${r.round}-${i}`}
                    className="rounded border border-slate-200 p-3 text-sm"
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs text-sky-600 font-medium">
                        {t(`common.skills.${q.axis}`)}
                      </span>
                      {q.difficulty && r.round === 2 && (
                        <span
                          className={`text-[10px] px-1 rounded ${
                            q.difficulty === "harder"
                              ? "bg-amber-100 text-amber-700"
                              : q.difficulty === "easier"
                                ? "bg-sky-100 text-sky-700"
                                : "bg-slate-100 text-slate-600"
                          }`}
                        >
                          {t(`admissions.interview.difficulty.${q.difficulty}`)}
                        </span>
                      )}
                      {q.interviewer_quick_rating && (
                        <span
                          className={`text-[10px] px-1.5 rounded ml-auto ${
                            RATING_TONES[q.interviewer_quick_rating] ?? "bg-slate-100 text-slate-600"
                          }`}
                        >
                          {t(`admissions.interview.ratings.${q.interviewer_quick_rating}`)}
                        </span>
                      )}
                    </div>
                    <p className="text-slate-800 mb-1">{q.question}</p>
                    {q.student_answer && (
                      <p className="text-xs text-slate-600 italic">
                        {t("classes.studentDetail.answerLabel")}{q.student_answer}
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-xs text-slate-500 italic">
          {t("classes.studentDetail.noTranscriptHint")}
        </p>
      )}
    </section>
  );
}

/** Inline status badge near the student name. Renders a tone-coded
 *  pill for every status including the default "active" — Jarod
 *  flagged the missing badge in v17. Tones map intent (emerald =
 *  enrolled / accepted, slate = withdrawn / inactive / graduated,
 *  amber = pre-decision funnel, rose = rejected). */
function StatusBadge({ status }: { status: string }) {
  const { t } = useTranslation();
  if (!status) return null;
  const tones: Record<string, string> = {
    active: "bg-emerald-100 text-emerald-700",
    inactive: "bg-slate-200 text-slate-700",
    candidate: "bg-amber-100 text-amber-700",
    interviewed: "bg-sky-100 text-sky-700",
    accepted: "bg-emerald-100 text-emerald-700",
    rejected: "bg-rose-100 text-rose-700",
    graduated: "bg-slate-100 text-slate-600",
    withdrawn: "bg-slate-100 text-slate-600",
  };
  return (
    <span
      className={`inline-block text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded ${
        tones[status] ?? "bg-slate-100 text-slate-600"
      }`}
    >
      {t(`common.status.${status}`, { defaultValue: status })}
    </span>
  );
}
